import {
  ExclamationCircleOutlined,
  PlusCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { Button, Empty, Input, Modal, Table } from 'antd';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import BreadCrumb from '../../../components/BreadCrumb';
import TabView from '../../../components/TabView';
import {
  deleteRiskIndicatorThresholdData,
  getRiskIndicatorsData,
  getRiskIndicatorStaticProperties,
  getRiskIndicatorThresholdsData,
  updateRiskIndicatorThresholdData,
  postRiskIndicatorTriggerScoring
} from '../../../redux/actions/crs/crsRiskIndicator';
import { hasResource } from '../../../util';
import { CRS_CONSTANTS } from '../../constants/crsContants';
import AddRuleModal from './AddRuleModal';
import styles from './index.scss';
import {
  getRiskIndicatorUpdates,
  getStaticProperty,
  getTableColumns,
  handleDeleteRuleNotification,
  handleUpdateRuleNotification,
  handlePostTriggerNotification,
  usePrevious
} from './util';
import { getSelectedResources } from '../../../util/resourceManager';

const { GET_DATA, POST_DATA } = CRS_CONSTANTS;
const { confirm } = Modal;
const { crsResources } = getSelectedResources();

// TNGD POC (FIN-12292): rules-only CRS — hide the ML-driven Inconsistency &
// Historical Behaviour tabs. Scoped to the POC build; do not merge to develop
// unconditionally (would remove these tabs for ML-CRS tenants).
const categoryList = ['Static', 'Regulatory'];

const isTriggerShown = true;

export const CrsRiskIndicator = ({
  dispatch,
  authentication,
  riskIndicators,
  riskIndicatorsTrigger,
  riskIndicatorThresholds,
  updateRiskIndicatorThreshold,
  deleteRiskIndicatorThreshold,
  riskIndicatorStaticProperties
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [addRuleVisible, setAddRuleVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState([]);
  const [validationErrorMessage, setValidationErrorMessage] = useState({});
  const [updateErrorMsg, setUpdateErrorMsg] = useState(true);

  const { indicatorsUpdates, errorMsg } = getRiskIndicatorUpdates(
    data,
    riskIndicators?.data
  );
  const prevRiskIndicatorsTrigger = usePrevious(riskIndicatorsTrigger);

  useEffect(() => {
    if (updateErrorMsg) {
      setValidationErrorMessage(errorMsg);
      setUpdateErrorMsg(false);
    }
  }, [errorMsg]);

  const prevUpdateRiskIndicatorThreshold = usePrevious(
    updateRiskIndicatorThreshold
  );
  const prevDeleteRiskIndicatorThreshold = usePrevious(
    deleteRiskIndicatorThreshold
  );

  const onNavigationTabSelect = (index) => {
    setSelectedTab(index);
  };

  const onPaginationChange = (page, pageSize) => {
    setValidationErrorMessage({});
    setUpdateErrorMsg(false);
    setCurrentPage(page);
    setPageSize(pageSize);
    fetchRiskIndicatorThresholds(page - 1, pageSize);
  };

  const onSearchPressEnter = () => {
    refreshRiskIndicatorThresholds();
  };

  const handleDataChange = (dataType, index, value) => {
    setData((previous) =>
      previous.map((d, i) =>
        i === index
          ? {
              ...d,
              [dataType]: value
            }
          : d
      )
    );
  };

  const handleConditionChange = (index, value) => {
    handleDataChange('newRiskCondition', index, value);
    setUpdateErrorMsg(true);
  };

  const handleThresholdChange = (index, value) => {
    handleDataChange('newRiskValues', index, value);
    setUpdateErrorMsg(true);
  };

  const handleWeightChange = (index, value) => {
    handleDataChange('newRiskWeight', index, Number(value));
  };

  const handleDeleteRuleConfirm = (record) => {
    confirm({
      title: 'Do you want to delete this rule?',
      icon: <ExclamationCircleOutlined />,
      content: `${record.riskIndicator}`,
      okText: 'Yes',
      okButtonProps: {
        loading: deleteRiskIndicatorThreshold.isLoading
      },
      cancelText: 'No',
      onOk() {
        dispatch(
          deleteRiskIndicatorThresholdData(
            {
              uuid: record.uuid
            },
            GET_DATA
          )
        );
      }
    });
  };

  const handleSaveButtonClick = () => {
    confirm({
      title: 'Do you want to save changes?',
      icon: <ExclamationCircleOutlined />,
      content: `Changes done in ${indicatorsUpdates.length} Risk Indicators will be Saved.`,
      okText: 'Save',
      onOk() {
        dispatch(
          updateRiskIndicatorThresholdData(
            { riskIndicators: indicatorsUpdates },
            GET_DATA
          )
        );
      }
    });
  };

  const handleTriggerButtonClick = () => {
    confirm({
      title: 'Do you want to trigger re-risk scoring?',
      icon: <ExclamationCircleOutlined />,
      content: `Re-risk scoring will be triggered`,
      okText: 'Save',
      onOk() {
        dispatch(postRiskIndicatorTriggerScoring({}, GET_DATA));
      }
    });
  };

  const handleCancelButtonClick = () => {
    confirm({
      title: 'Do you want to cancel changes?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Changes done in any Risk Indicator Thresholds will be reverted back to the data saved in the previous edit.',
      onOk() {
        setData((previous) =>
          previous.map(
            ({
              uuid,
              category,
              riskIndicator,
              riskCondition,
              riskValues,
              riskWeight
            }) => ({
              uuid,
              category,
              riskIndicator,
              riskCondition,
              riskValues,
              riskWeight
            })
          )
        );
      }
    });
  };

  const fetchRiskIndicatorThresholds = (
    page,
    pageSize,
    enableSearch = true
  ) => {
    let category = categoryList[selectedTab].replace(/ /g, '_');

    // Convert text into uppercase if the Selected Tab is Historical Behaviour
    if (categoryList[selectedTab] === 'Historical Behaviour')
      category = category.toUpperCase();
    const payload = {
      category,
      limit: pageSize,
      offset: page * pageSize
    };
    if (enableSearch && searchText.trim()) payload.searchText = searchText;
    dispatch(getRiskIndicatorThresholdsData(payload, GET_DATA));
  };

  const refreshRiskIndicatorThresholds = (enableSearch = true) => {
    setCurrentPage(1);
    fetchRiskIndicatorThresholds(0, pageSize, enableSearch);
  };

  useEffect(() => {
    refreshRiskIndicatorThresholds(false);
    setSearchText('');
    setValidationErrorMessage({});
    setUpdateErrorMsg(false);
  }, [selectedTab]);

  useEffect(() => {
    if (riskIndicatorThresholds.data?.indicators) {
      setData(riskIndicatorThresholds.data.indicators);
    } else {
      setData([]);
    }
  }, [riskIndicatorThresholds]);

  useEffect(() => {
    if (prevUpdateRiskIndicatorThreshold?.isLoading) {
      const { data, isLoading, error } = updateRiskIndicatorThreshold;
      if (!isLoading) {
        fetchRiskIndicatorThresholds(currentPage - 1, pageSize);
        handleUpdateRuleNotification(error?.data?.msg);
      }
    }
  }, [updateRiskIndicatorThreshold]);

  useEffect(() => {
    if (prevRiskIndicatorsTrigger?.isLoading) {
      const { data, isLoading, error } = riskIndicatorsTrigger;
      if (!isLoading) {
        handlePostTriggerNotification(data?.pipelineId, error ?? data?.msg);
      }
    }
  }, [riskIndicatorsTrigger]);

  useEffect(() => {
    if (prevDeleteRiskIndicatorThreshold?.isLoading) {
      const { data, isLoading } = deleteRiskIndicatorThreshold;
      if (typeof data !== 'object' && !isLoading) {
        refreshRiskIndicatorThresholds();
        handleDeleteRuleNotification(data);
      }
    }
  }, [deleteRiskIndicatorThreshold]);

  useEffect(() => {
    dispatch(getRiskIndicatorStaticProperties({}, GET_DATA));
  }, []);

  useEffect(() => {
    if (selectedTab < 2)
      dispatch(
        getRiskIndicatorsData({ category: categoryList[selectedTab] }, GET_DATA)
      );
  }, [categoryList[selectedTab]]);

  const handleSearchChange = (e) => {
    // Remove any tabs, new lines, or carriage returns from the input
    const input = e.target.value.replace(/[\t\n\r]/g, '').trim();
    const pattern = new RegExp(/^[a-zA-Z0-9- /_()]+$/);

    if (pattern.test(input) || input === '') {
      setSearchText(input);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.navPanel}>
        <div className={styles.breadcrumb}>
          <BreadCrumb
            crumbs={[
              // { label: 'Settings', linkTo: 'settings' },
              { label: 'Risk Indicator Threshold' }
            ]}
          />
        </div>
        <div className={styles.pageTitle}>
          Risk Indicator Threshold Configuration
        </div>
      </div>
      <div className={styles.tabViewContainer}>
        <TabView
          defaultIndex={0}
          tabList={categoryList}
          onTabSelect={onNavigationTabSelect}
        />
      </div>
      <div className={styles.card}>
        <div
          className={
            styles.title
          }>{`${categoryList[selectedTab]} Risk Indicators`}</div>
        <div className={styles.toolBar}>
          <div className={styles.searchInput}>
            <label className={styles.label}>Search</label>
            <Input
              size="small"
              value={searchText}
              placeholder="Search for Risk Indicator"
              prefix={<SearchOutlined />}
              onChange={handleSearchChange}
              onPressEnter={onSearchPressEnter}
              // maxLength={100}
            />
          </div>
        </div>
        <Table
          className={styles.table}
          dataSource={data}
          columns={getTableColumns(
            selectedTab,
            riskIndicators?.data,
            handleConditionChange,
            handleThresholdChange,
            handleWeightChange,
            handleDeleteRuleConfirm,
            riskIndicatorThresholds.isLoading,
            validationErrorMessage,
            hasResource(
              authentication?.data,
              crsResources.APP_3_RESOURCE_UI_CRS_CONFIGURE_RULES
            )
          )}
          rowKey="uuid"
          loading={riskIndicatorThresholds.isLoading}
          footer={() =>
            selectedTab < 2 &&
            hasResource(
              authentication?.data,
              crsResources.APP_3_RESOURCE_UI_CRS_CONFIGURE_RULES
            ) && (
              <div
                className={classnames(styles.footer, {
                  [styles.marginedFooter]: !data.length
                })}>
                <Button
                  type="text"
                  icon={<PlusCircleOutlined className={styles.addIcon} />}
                  onClick={() => {
                    setAddRuleVisible(true);
                  }}>
                  Add Rule
                </Button>
                <AddRuleModal
                  category={categoryList[selectedTab]}
                  addRuleVisible={addRuleVisible}
                  setAddRuleVisible={setAddRuleVisible}
                  riskIndicators={riskIndicators}
                  refreshRiskIndicatorThresholds={
                    refreshRiskIndicatorThresholds
                  }
                />
              </div>
            )
          }
          pagination={{
            total: riskIndicatorThresholds.data?.total,
            current: currentPage,
            pageSize,
            onChange: onPaginationChange,
            showTotal: (total) => `${total} Records`
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No results found"
              />
            )
          }}
        />
        <div className={styles.currencyHint}>
          {`*All currency values are in ${getStaticProperty(
            riskIndicatorStaticProperties.data?.properties,
            'Currency'
          )}`}
        </div>
      </div>
      <div className={styles.actions}>
        {isTriggerShown &&
          hasResource(
            authentication?.data,
            crsResources.APP_3_RESOURCE_UI_CRS_TRIGGER_RE_SCORING
          ) && (
            <Button
              disabled={riskIndicatorsTrigger.isLoading}
              type="primary"
              onClick={handleTriggerButtonClick}>
              Trigger Re-risk Scoring
            </Button>
          )}
        {hasResource(
          authentication?.data,
          crsResources.APP_3_RESOURCE_UI_CRS_CONFIGURE_RULES
        ) && (
          <Button
            className={styles.cancelButton}
            disabled={indicatorsUpdates.length === 0}
            onClick={handleCancelButtonClick}>
            Cancel
          </Button>
        )}
        {hasResource(
          authentication?.data,
          crsResources.APP_3_RESOURCE_UI_CRS_CONFIGURE_RULES
        ) && (
          <Button
            className={styles.saveButton}
            type="primary"
            disabled={indicatorsUpdates.length === 0}
            onClick={handleSaveButtonClick}>
            Save
          </Button>
        )}
      </div>
    </div>
  );
};

CrsRiskIndicator.propTypes = {
  dispatch: PropTypes.func,
  authentication: PropTypes.object,
  riskIndicators: PropTypes.object,
  riskIndicatorThresholds: PropTypes.object,
  riskIndicatorsTrigger: PropTypes.any,
  updateRiskIndicatorThreshold: PropTypes.any,
  deleteRiskIndicatorThreshold: PropTypes.any,
  riskIndicatorStaticProperties: PropTypes.object
};

function mapStateToProps(state, ownProps = {}) {
  return {
    authentication: state.authentication,
    riskIndicators: state.crsRiskIndicator.riskIndicators,
    riskIndicatorThresholds: state.crsRiskIndicator.riskIndicatorThresholds,
    riskIndicatorsTrigger: state.crsRiskIndicator.riskIndicatorsTrigger,
    updateRiskIndicatorThreshold:
      state.crsRiskIndicator.updateRiskIndicatorThreshold,
    deleteRiskIndicatorThreshold:
      state.crsRiskIndicator.deleteRiskIndicatorThreshold,
    riskIndicatorStaticProperties:
      state.crsRiskIndicator.riskIndicatorStaticProperties
  };
}

function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CrsRiskIndicator);
